import React, {useContext, useEffect, useMemo, useState} from "react";
import {AiExtractorServiceContext, AppContextContext} from "../../Contexts";
import {Breadcrumb, Button, message, Modal, Space, Table, Tag} from "antd";
import {DeleteFilled, PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import {Link, useLocation, useNavigate} from "react-router-dom";
import MarkdownContent from "../common/MarkdownContent";
import _ from "lodash";
import * as globalStyles from "../App.module.css";
import * as styles from "./AiExtractorList.module.css";
import AiExtractor, {AiExtractorType, AiExtractorWebSearchMode, formatAiExtractorType, formatAiExtractorWebSearchMode, formatExternalWebSearchBackend, isActivelyManagedByInterests} from "../../domain/AiExtractor";
import {useTableHandler} from "../../sal-ui/TableHandler";
import PagedResult from "../../service/PagedResult";
import {DocumentTitle} from "../DocumentTitle";
import {ColumnsType} from "antd/es/table";
import {formatAiExtractorSource} from "../../domain/AiExtractorSource";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import {Permission} from "../../domain/auth/Permission";
import {UserRole} from "../../domain/UserRole";
import {SingleSummaryBlock} from "../common/SummaryBlock";
import {renderOrganizationLink} from "../common/OrganizationLink";
import Tip from "../common/Tip";
import {FormPersister} from "../../sal-ui/FormPersister";
import OwnerFilter, {createDefaultOwnerFilterValues, createOwnerRqbQuery, loadPersistentOwnerFilterValues} from "../common/OwnerFilter";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";

const PERSISTENT_IDENT = "AiExtractorList";
const FILTER_DEBOUNCE_MS = 500;

function buildModelTagLabels(aiExtractor: AiExtractor, isSystemAdmin: boolean): string[] {
    const labels: string[] = [];

    if ((aiExtractor.type === AiExtractorType.OPENAI_API
            || aiExtractor.type === AiExtractorType.OPENAI_COMPATIBLE_API)
        && isSystemAdmin
        && aiExtractor.openAiServiceTier) {
        labels.push(`service tier: ${String(aiExtractor.openAiServiceTier).toLowerCase()}`);
    }

    if ((aiExtractor.type === AiExtractorType.OPENAI_API || aiExtractor.type === AiExtractorType.OPENAI_COMPATIBLE_API)
        && aiExtractor.openAiReasoningEffort) {
        labels.push(`reasoning: ${String(aiExtractor.openAiReasoningEffort).toLowerCase()}`);
    }

    if (aiExtractor.type === AiExtractorType.OPENAI_COMPATIBLE_API && aiExtractor.aiProviderConnection?.name) {
        labels.push(`provider: ${aiExtractor.aiProviderConnection.name}`);
    }

    if (aiExtractor.type === AiExtractorType.GROK_API && aiExtractor.grokReasoningEffort) {
        labels.push(`reasoning: ${String(aiExtractor.grokReasoningEffort).toLowerCase()}`);
    }

    if (aiExtractor.type === AiExtractorType.ANTHROPIC_API && aiExtractor.anthropicReasoningEffort) {
        labels.push(`reasoning: ${String(aiExtractor.anthropicReasoningEffort).toLowerCase()}`);
    }

    if (aiExtractor.webSearchMode && aiExtractor.webSearchMode !== AiExtractorWebSearchMode.DISABLED) {
        labels.push(`web: ${formatAiExtractorWebSearchMode(aiExtractor.webSearchMode)}`);
    }

    if (aiExtractor.webSearchMode === AiExtractorWebSearchMode.EXTERNAL && aiExtractor.externalWebSearchBackend) {
        labels.push(`backend: ${formatExternalWebSearchBackend(aiExtractor.externalWebSearchBackend)}`);
    }

    return labels;
}

function renderModelTags(aiExtractor: AiExtractor, isSystemAdmin: boolean) {
    const labels = buildModelTagLabels(aiExtractor, isSystemAdmin);

    if (labels.length === 0) {
        return null;
    }

    return (
        <div className={styles["model-tags"]}>
            {labels.map(label => <Tag key={label}>{label}</Tag>)}
        </div>
    );
}

function AiExtractorList() {
    const appContext = useContext(AppContextContext);
    const aiExtractorService = useContext(AiExtractorServiceContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [aiExtractors, setAiExtractors] = useState<PagedResult<AiExtractor>>();
    const [filterValues, setFilterValues] = useState<any>();
    const filterFormPersister = useMemo(() => new FormPersister(PERSISTENT_IDENT), []);
    const debouncedOnFinishFilter = useMemo(() => _.debounce(onFinishFilter, FILTER_DEBOUNCE_MS), []);

    const title = "AI extractors";
    const hasManage = appContext.user.isSystemAdmin() || appContext.user.hasPermission(Permission.AI_EXTRACTOR__MANAGE_ORG);
    const hasOrganizationAccess = appContext.user.hasPermission(Permission.AI_EXTRACTOR__READ_ORG)
                                  || appContext.user.hasPermission(Permission.AI_EXTRACTOR__MANAGE_ORG);
    const ownerFilterAccess = {
        canReadSystem: appContext.user.hasPermission(Permission.AI_EXTRACTOR__READ_SEAL) || hasOrganizationAccess,
        canReadOrganization: hasOrganizationAccess
    };

    const tableHandler = useTableHandler("name", {
        reloadFunction: reload,
        persistentIdent: PERSISTENT_IDENT
    });

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const values = loadPersistentOwnerFilterValues({
            appContext,
            access: ownerFilterAccess,
            filterFormPersister,
            location,
            navigate
        });

        setFilterValues(values);
    }, []);

    useEffect(() => {
        if (filterValues !== undefined) {
            debouncedOnFinishFilter(filterValues);
        }
    }, [filterValues]);

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title}
                            ]}
                />

                <h1>{title}</h1>

                <Tip
                    id="AiExtractorList.purpose"
                    style={{marginBottom: 12}}
                    message="AI extractors define how SEAL uses large language models (LLM) to extract values from vulnerability items."
                    description={
                        <>
                            SEAL uses them to populate{" "}
                            <Link to="/definitions/ai-attributes">AI attributes</Link> for individual items. Some extractors are managed automatically from{" "}
                            <Link to="/interests">interests</Link>.
                        </>
                    }
                />

                <OwnerFilter
                    className={styles.filter}
                    values={filterValues}
                    defaultValues={createDefaultOwnerFilterValues(appContext, ownerFilterAccess)}
                    persistentStateIdent={PERSISTENT_IDENT}
                    canReadSystem={ownerFilterAccess.canReadSystem}
                    canReadOrganization={ownerFilterAccess.canReadOrganization}
                    onChange={setFilterValues}
                />

                <div className={globalStyles["common__top-button-bar"]}>
                    {hasManage &&
                        <Button type={"primary"}
                                icon={<PlusOutlined/>}
                                onClick={() => navigate("/definitions/ai-extractors/add")}>
                            Add
                        </Button>
                    }
                    <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>
                </div>

                <Table
                    className={styles.table}
                    dataSource={aiExtractors?.data}
                    loading={tableHandler.loading}
                    pagination={tableHandler.pagination}
                    showSorterTooltip={false}
                    rowKey="id"
                    onChange={tableHandler.onTableChange}
                    columns={getColumns()}
                />
            </>
        </DocumentTitle>
    );

    function getColumns(): ColumnsType<AiExtractor> {
        const cols: ColumnsType<AiExtractor> = [
            {
                dataIndex: "name",
                title: "Name",
                sorter: true,
                sortDirections: ascendingFirstSortDirections,
                defaultSortOrder: "ascend",
                render: (value, aiExtractor: AiExtractor) =>
                    <div className={styles["name-cell"]}>
                        <div>
                            <Link to={`/definitions/ai-extractors/${aiExtractor.id}`}>{value}</Link>
                            {isActivelyManagedByInterests(aiExtractor) && (
                                <Tag color="blue"
                                     style={{marginLeft: 8}}
                                     title="This AI extractor is managed automatically through Interests.">
                                    managed
                                </Tag>
                            )}
                        </div>

                        {aiExtractor.description && (
                            <SingleSummaryBlock>
                                <MarkdownContent children={aiExtractor.description}/>
                            </SingleSummaryBlock>
                        )}

                        {aiExtractor.aiAttributes && aiExtractor.aiAttributes.length > 0 && (
                            <div className={styles["ai-attribute-tags"]}>
                                {aiExtractor.aiAttributes.map(aiAttribute => (
                                    <Tag key={`${aiExtractor.id}-${aiAttribute.id || aiAttribute.name}`}
                                         bordered={true}>
                                        {aiAttribute.name || aiAttribute.id}
                                    </Tag>
                                ))}
                            </div>
                        )}

                        {(aiExtractor.systemPrompt?.name || aiExtractor.supplementaryPrompt?.name || aiExtractor.source) && (
                            <div className={styles["ai-extractor-tags"]}>
                                {aiExtractor.source && (
                                    <Tag color="green" bordered={true}>{formatAiExtractorSource(aiExtractor.source)}</Tag>
                                )}
                                {aiExtractor.systemPrompt?.name && (
                                    <Tag color="blue" bordered={true}>{aiExtractor.systemPrompt.name}</Tag>
                                )}
                                {aiExtractor.supplementaryPrompt?.name && (
                                    <Tag color="cyan" bordered={true}>+ {aiExtractor.supplementaryPrompt.name}</Tag>
                                )}
                            </div>
                        )}
                    </div>
            },
            {
                dataIndex: ["organization", "name"],
                key: "organization.name",
                title: "Owner",
                sorter: true,
                sortDirections: ascendingFirstSortDirections,
                align: "left",
                render: renderOrganization
            },
            {dataIndex: "type", title: "Type", width: 160, render: value => formatAiExtractorType(value)},
            {
                dataIndex: "model",
                title: "Model",
                render: (value, aiExtractor: AiExtractor) => (
                    <div className={styles["model-cell"]}>
                        <div>{value || <i>no model</i>}</div>
                        {renderModelTags(aiExtractor, appContext.user.isSystemAdmin())}
                    </div>
                )
            },
            {
                dataIndex: "vulSources",
                title: "Sources",
                render: (value: AiExtractor["vulSources"]) =>
                    value && value.length > 0
                        ? value.map(vs => <div key={vs.id}><Link to={`/vul-sources/${vs.id}`}>{vs.name}</Link></div>)
                        : null
            },
        ];

        if (hasManage) {
            cols.push({
                title: "Actions",
                width: 100,
                className: globalStyles["table-actions"],
                render: renderAction
            });
        }

        return cols;
    }

    function renderOrganization(value: any, aiExtractor: AiExtractor) {
        if (aiExtractor.owner === ResourceOwnerType.SYSTEM) {
            return <i>System</i>;
        }

        return renderOrganizationLink(aiExtractor.organization, value);
    }

    function renderAction(_value: unknown, aiExtractor: AiExtractor) {
        if (!isEditableForPrincipal(aiExtractor)) {
            return null;
        }

        return (
            <Space>
                <Button
                    icon={<DeleteFilled/>}
                    className={"ant-btn-icon-only"}
                    title={"Delete"}
                    onClick={() => {
                        Modal.confirm({
                            content: <>Do you really want to delete AI extractor <b>{aiExtractor.name}</b>?</>,
                            okText: "Delete",
                            cancelText: "Cancel",
                            onOk: () => onDeleteConfirm(aiExtractor)
                        });
                    }}
                />
            </Space>
        );
    }

    function isEditableForPrincipal(aiExtractor: AiExtractor) {
        if (isActivelyManagedByInterests(aiExtractor)) {
            return false;
        }

        return aiExtractor?.owner !== ResourceOwnerType.SYSTEM || appContext.user.role === UserRole.SYSTEM_ADMIN;
    }

    function onDeleteConfirm(aiExtractor: AiExtractor) {
        aiExtractorService.delete(aiExtractor)
            .then(() => {
                message.success("AI extractor deleted.");

                tableHandler.reload();
            });
    }

    function onFinishFilter(values: any) {
        const rqbQuery = createOwnerRqbQuery(values);

        filterFormPersister.saveState(values);

        tableHandler.onRqbSearchSubmit(rqbQuery);
    }

    function reload() {
        return aiExtractorService.getList(tableHandler.queryOptions)
            .then(result => {
                tableHandler.updateTotal(result.total);

                setAiExtractors(result);
            });
    }
}

export default AiExtractorList;
